import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  CalendarCheck,
  CalendarDays,
  CircleCheck,
  CircleDashed,
  CircleDot,
  Clock,
  Copy,
  ExternalLink,
  Eye,
  Flag,
  Hash,
  Hourglass,
  Inbox,
  ListChecks,
  MessageSquare,
  Play,
  RefreshCw,
  Star,
  Ticket,
  User,
} from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateTime, formatDayLabel } from '@/lib/dates'
import { cn } from '@/lib/utils'
import { useGenerateMeetingPrep, useMeetingPrep, useTickMeetingPrep } from '@/queries/meetingPrep'
import { extractErrorMessage } from '@/queries/ticketMutations'
import { callTimeLabel, daysUntilLabel, nextCallLabel } from '@/types/clientMeetings'
import { DUE_PREFIX, STAGE_LABELS, STAGE_ORDER, SUMMARY_TAGS, itemText, prepAsText, sectionTitle } from '@/types/meetingPrep'
import type { MeetingPrep, PrepItem, PrepRef, PrepStage } from '@/types/meetingPrep'
import { CountChip, sectionLabelClass } from './dashboardParts'
import { clientTabPath } from './tabs'

const SHOW_FIRST = 15
const AMBER_DAYS = 30
const RED_DAYS = 60

const STAGE_ICONS: Record<PrepStage, ReactNode> = {
  progress: <CircleDot className="size-4 text-etn-accent-text-active" />,
  sprint: <CalendarDays className="size-4 text-etn-info-text" />,
  backlog: <Inbox className="size-4 text-etn-text-label" />,
  triage: <CircleDashed className="size-4 text-etn-text-soft" />,
}

function statusIcon(item: PrepItem): ReactNode {
  if (item.section === 'delivered') return <CircleCheck className="size-4 text-etn-success-text" />
  if (item.section === 'need') return <Hourglass className="size-4 text-etn-warning-text" />
  if (item.section === 'promised') return <ListChecks className="size-4 text-etn-accent-text-active" />
  if (item.section === 'helpdesk') return <Ticket className="size-4 text-etn-accent-text-active" />
  if (item.section === 'unlogged') return <MessageSquare className="size-4 text-etn-text-label" />
  if (item.stage === 'progress' && /review/i.test(item.stateLabel ?? '')) return <Eye className="size-4 text-etn-info-text" />
  return item.stage ? STAGE_ICONS[item.stage] : null
}

const ageVariant = (days: number) => (days > RED_DAYS ? 'destructive' : days > AMBER_DAYS ? 'warning' : 'neutral')

function Meta({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11.5px] text-etn-text-label [&>svg]:size-3">
      {icon}
      {children}
    </span>
  )
}

/** A ref that opens its ticket in the help desk or its issue in Linear, with an icon to open it in a new tab. */
function RefLink({ prepRef, className, inline = false }: { prepRef: PrepRef; className?: string; inline?: boolean }) {
  return (
    <span className={cn('inline-flex items-center align-baseline', inline ? 'gap-0.5' : 'gap-1')}>
      {prepRef.kind === 'ticket' ? (
        <Link to={`/admin/tickets/${prepRef.id}` as never} className={cn('font-semibold text-etn-accent-text-active hover:underline', className)}>
          {prepRef.ref}
        </Link>
      ) : (
        <a href={prepRef.url} target="_blank" rel="noreferrer" className={cn('font-semibold text-etn-text-cell hover:underline', className)}>
          {prepRef.ref}
        </a>
      )}
      <a
        href={prepRef.kind === 'ticket' ? `/admin/tickets/${prepRef.id}` : prepRef.url}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open ${prepRef.ref} in a new tab`}
        title={`Open ${prepRef.ref} in a new tab`}
        className={cn('inline-grid place-items-center rounded-etn-pill text-etn-text-soft hover:bg-etn-control-subtle hover:text-etn-text', inline ? 'size-3.5' : 'size-5')}
      >
        <ExternalLink className="size-3" />
      </a>
    </span>
  )
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Turn each ref the prep's lists know into a link, longest first so GER-41 is never read as GER-4.
function LinkedText({ text, refs }: { text: string; refs: Map<string, PrepRef> }) {
  if (!refs.size) return <>{text}</>
  const pattern = new RegExp(`(?<![A-Za-z0-9-])(${[...refs.keys()].sort((a, b) => b.length - a.length).map(escapeRegExp).join('|')})(?![A-Za-z0-9])`)
  return (
    <>
      {text.split(pattern).map((part, index) => {
        const prepRef = index % 2 ? refs.get(part) : undefined
        return prepRef ? <RefLink key={index} prepRef={prepRef} inline /> : part
      })}
    </>
  )
}

function PrepLine({ item, onTick }: { item: PrepItem; onTick: (item: PrepItem) => void }) {
  return (
    <li data-testid={`prep-item-${item.key}`} className="grid grid-cols-[18px_18px_1fr] items-start gap-2.5 py-2.5">
      <button
        type="button"
        role="checkbox"
        aria-checked={item.ticked}
        aria-label={`Tick ${itemText(item)}`}
        onClick={() => onTick(item)}
        className={cn(
          'mt-0.5 grid size-4 place-items-center rounded-[4px] border-[1.5px] text-[10px] text-white',
          item.ticked ? 'border-etn-accent bg-etn-accent' : 'border-[var(--etn-gray-300)] bg-etn-surface'
        )}
      >
        {item.ticked && '✓'}
      </button>
      <span className={cn('mt-0.5 inline-flex', item.ticked && 'opacity-40')} title={item.stateLabel ?? undefined}>
        {statusIcon(item)}
      </span>
      <div className="min-w-0">
        <div className={cn('text-[13px] font-medium leading-snug text-etn-text', item.ticked && 'text-etn-text-soft line-through')} title={item.wording ? item.title : undefined}>
          {itemText(item)}
        </div>
        <div className={cn(item.ticked && 'opacity-45')}>
          {item.highlight && (
            <div className="mt-1 inline-flex items-center gap-1 text-[12px] text-etn-info-text">
              <Star className="size-3" />
              Worth an update: {item.highlight}
            </div>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1">
            {item.refs.map(ref => (
              <RefLink key={ref.id} prepRef={ref} className="text-[11.5px]" />
            ))}
            {item.stage === 'progress' && item.stateLabel && <span className="text-[11.5px] text-etn-text-label">{item.stateLabel}</span>}
            {item.due && (
              <Badge variant="info">
                <CalendarCheck />
                {DUE_PREFIX[item.due.kind]} {formatDayLabel(item.due.date)}
              </Badge>
            )}
            {item.waitingDays !== null && (
              <Badge variant={ageVariant(item.waitingDays)}>
                <Clock />
                {item.waitingDays} days
              </Badge>
            )}
            {item.openDays !== null && <Meta icon={<Clock />}>Open {item.openDays} days</Meta>}
            {item.person && <Meta icon={<User />}>{item.person}</Meta>}
            {item.moment && (
              <a href={item.moment.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11.5px] font-medium text-etn-accent-text-active hover:underline">
                <Play className="size-3" />
                at {callTimeLabel(item.moment.seconds)}
              </a>
            )}
            {item.flag && (
              <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-etn-danger-text">
                <Flag className="size-3" />
                {item.flag}
              </span>
            )}
          </div>
        </div>
      </div>
    </li>
  )
}

// Split items into runs, so each run of items sharing an AI group shows as one block.
const runs = (items: PrepItem[]) =>
  items.reduce<{ group: string | null; items: PrepItem[] }[]>((all, item) => {
    const last = all[all.length - 1]
    return last && item.group && last.group === item.group ? [...all.slice(0, -1), { ...last, items: [...last.items, item] }] : [...all, { group: item.group, items: [item] }]
  }, [])

function Lines({ items, onTick }: { items: PrepItem[]; onTick: (item: PrepItem) => void }) {
  return (
    <div className="divide-y divide-etn-border-row">
      {runs(items).map(run =>
        run.group ? (
          <div key={run.items[0].key} role="group" aria-label={run.group} className="py-2">
            <div className="text-[12px] font-semibold text-etn-text-label">{run.group}</div>
            <ul className="ml-1 mt-1 divide-y divide-etn-border-row border-l-2 border-etn-border-hairline pl-3">
              {run.items.map(item => (
                <PrepLine key={item.key} item={item} onTick={onTick} />
              ))}
            </ul>
          </div>
        ) : (
          <ul key={run.items[0].key}>
            <PrepLine item={run.items[0]} onTick={onTick} />
          </ul>
        )
      )}
    </div>
  )
}

function PrepSectionBlock({ title, items, onTick, footer }: { title: string; items: PrepItem[]; onTick: (item: PrepItem) => void; footer?: ReactNode }) {
  const [expanded, setExpanded] = useState(false)
  const shown = expanded ? items : items.slice(0, SHOW_FIRST)
  const staged = items.some(item => item.stage)
  return (
    <section aria-label={title} className="mb-7">
      <div className="mb-1.5 flex items-center gap-2 border-b border-etn-border-hairline pb-2">
        <h3 className="text-[13.5px] font-semibold text-etn-text">{title}</h3>
        <CountChip>{items.length}</CountChip>
      </div>
      {staged
        ? STAGE_ORDER.map(stage => {
            const inStage = shown.filter(item => item.stage === stage)
            if (!inStage.length) return null
            return (
              <div key={stage}>
                <div className={cn(sectionLabelClass, 'mt-3 flex items-center gap-1.5 [&>svg]:size-3.5')}>
                  {STAGE_ICONS[stage]}
                  {STAGE_LABELS[stage]}
                  <span className="text-etn-text-soft">{items.filter(item => item.stage === stage).length}</span>
                </div>
                <Lines items={inStage} onTick={onTick} />
              </div>
            )
          })
        : <Lines items={shown} onTick={onTick} />}
      {items.length > SHOW_FIRST && !expanded && (
        <Button variant="link" size="sm" className="px-0" onClick={() => setExpanded(true)}>
          Show {items.length - SHOW_FIRST} more
        </Button>
      )}
      {footer}
    </section>
  )
}

function StatusBar({ prep, regenerating, onRegenerate }: { prep: MeetingPrep; regenerating: boolean; onRegenerate: () => void }) {
  const notes = prep.notes
  if (!notes) return null
  const stale = !notes.upToDate
  return (
    <div data-testid="prep-status" className={cn('mb-5 flex items-start justify-between gap-4 rounded-etn-card px-3.5 py-3', stale ? 'bg-etn-warning-subtle' : 'bg-etn-accent-tint')}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          <b className="font-semibold text-etn-text">Summary written {formatDateTime(notes.generatedAt)}</b>
          {notes.summaryFailed ? (
            <Badge variant="warning">Summary not written</Badge>
          ) : stale ? (
            <Badge variant="warning">{notes.changes.length === 1 ? '1 change since' : `${notes.changes.length} changes since`}</Badge>
          ) : (
            <Badge variant="success">Up to date</Badge>
          )}
        </div>
        {stale && notes.changes.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {notes.changes.map(change => (
              <span key={change} className="rounded-etn-pill bg-white/70 px-2.5 py-0.5 text-[11.5px] text-etn-warning-text">
                {change}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-2">
        <Button size="sm" disabled={regenerating} onClick={onRegenerate}>
          <RefreshCw />
          {regenerating ? 'Regenerating…' : 'Regenerate'}
        </Button>
        <Meta icon={<Hash />}>
          {prep.postedAt ? `Posted to #cs ${formatDateTime(prep.postedAt)}` : prep.nextCallAt ? 'Posts to #cs an hour before the call' : 'Not posted: no next call set'}
        </Meta>
      </div>
    </div>
  )
}

function Summary({ prep }: { prep: MeetingPrep }) {
  const notes = prep.notes
  if (!notes) return null
  const refs = new Map(prep.sections.flatMap(section => section.items.flatMap(item => item.refs.map(ref => [ref.ref, ref] as const))))
  return (
    <section aria-label="Before the call" className="mb-7 rounded-etn-card border border-etn-border-hairline px-4 pb-1.5 pt-3.5">
      <div className="mb-2.5 flex items-center gap-2 border-b border-etn-border-hairline pb-2">
        <h3 className="text-[13.5px] font-semibold text-etn-text">Before the call</h3>
        <span className="text-[12px] text-etn-text-soft">Written by AI from the lists below</span>
      </div>
      {notes.summaryFailed ? (
        <p className="pb-2.5 text-[13px] text-etn-text-label">The summary couldn't be written this time. Regenerate to try again; the lists below are complete.</p>
      ) : (
        notes.summary.map(bullet => (
          <div key={bullet.text} className="grid grid-cols-[92px_1fr] items-baseline gap-2.5 pb-2.5 text-[13px] leading-normal">
            <Badge variant={SUMMARY_TAGS[bullet.tag].variant} className="w-full justify-center">
              {SUMMARY_TAGS[bullet.tag].label}
            </Badge>
            <div className="text-etn-text">
              <LinkedText text={bullet.text} refs={refs} />
            </div>
          </div>
        ))
      )}
    </section>
  )
}

export function MeetingPrepDialog({ companyId, clientName }: { companyId: string; clientName: string }) {
  const navigate = useNavigate()
  const prepQuery = useMeetingPrep(companyId)
  const generate = useGenerateMeetingPrep(companyId)
  const tick = useTickMeetingPrep(companyId)
  const asked = useRef(false)
  const prep = prepQuery.data?.data

  // Write the notes the first time they're opened; after that, only Regenerate writes new ones.
  useEffect(() => {
    if (prep && !prep.notes && !asked.current) {
      asked.current = true
      generate.mutate({})
    }
  }, [prep, generate])

  const close = () => navigate({ to: '.', search: ((previous: Record<string, unknown>) => ({ ...previous, prep: undefined })) as never, replace: true } as never)
  const onTick = (item: PrepItem) => (prep?.notes ? tick.mutate({ key: item.key, ticked: !item.ticked }) : toast.info('The notes are still being written'))
  const copy = async () => {
    if (!prep) return
    if (typeof navigator.clipboard?.writeText !== 'function') {
      toast.error('Clipboard unavailable in this browser context')
      return
    }
    try {
      await navigator.clipboard.writeText(prepAsText(prep, clientName))
      toast.success('Prep notes copied')
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Could not copy the prep notes'))
    }
  }
  const next = nextCallLabel(prep?.nextCallAt ?? null)
  const writing = generate.isPending

  return (
    <Dialog open onOpenChange={open => !open && close()}>
      <DialogContent className="flex max-h-[calc(100vh-4rem)] flex-col gap-0 p-0 sm:max-w-[880px]">
        <DialogHeader className="border-b border-etn-border-hairline px-6 pb-3.5 pt-5">
          <DialogTitle>Prepare meeting</DialogTitle>
          <DialogDescription>
            {clientName} · {next && prep?.nextCallAt ? `${next} · ${daysUntilLabel(prep.nextCallAt)}` : 'No next call set'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {!prep ? (
            prepQuery.isError ? (
              <p className="text-[13px] text-etn-text-label">The prep didn't load. Close and try again.</p>
            ) : (
              <Skeleton className="h-40 w-full" />
            )
          ) : (
            <>
              {writing && !prep.notes ? (
                <div data-testid="prep-writing" className="mb-5 rounded-etn-card bg-etn-accent-tint px-3.5 py-3 text-[13px] text-etn-text">
                  Writing the summary. The lists below are ready now.
                </div>
              ) : !prep.notes ? (
                <div className="mb-5 flex items-center justify-between gap-4 rounded-etn-card bg-etn-warning-subtle px-3.5 py-3 text-[13px] text-etn-text">
                  The summary hasn't been written yet. The lists below are ready now.
                  <Button size="sm" onClick={() => generate.mutate({})}>
                    <RefreshCw />
                    Write the summary
                  </Button>
                </div>
              ) : (
                <StatusBar prep={prep} regenerating={writing} onRegenerate={() => generate.mutate({ force: true })} />
              )}
              <Summary prep={prep} />
              {prep.sections
                .filter(section => section.items.length)
                .map(section => (
                  <PrepSectionBlock
                    key={section.section}
                    title={sectionTitle(section.section, prep, clientName)}
                    items={section.items}
                    onTick={onTick}
                    footer={
                      section.section === 'unlogged' && prep.olderNotLogged > 0 ? (
                        <Link to={clientTabPath(companyId, 'not-logged') as never} className="mt-2 inline-block text-[12.5px] font-medium text-etn-accent-text-active hover:underline">
                          {prep.olderNotLogged === 1 ? '1 older request not logged' : `${prep.olderNotLogged} older requests not logged`}
                        </Link>
                      ) : undefined
                    }
                  />
                ))}
              {prep.sections.every(section => !section.items.length) && <p className="text-[13px] text-etn-text-label">Nothing open, waiting or delivered for {clientName} right now.</p>}
            </>
          )}
        </div>
        <DialogFooter className="items-center border-t border-etn-border-hairline px-6 py-3 sm:justify-between">
          <span className="text-[12px] text-etn-text-soft">Cleared when the next call comes in from Fireflies</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={!prep} onClick={copy}>
              <Copy />
              Copy
            </Button>
            <Button size="sm" onClick={close}>
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
